import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import CustomerFormDialog from "@/components/CustomerFormDialog";
import OrderFormDialog from "@/components/OrderFormDialog";
import ContactFormDialog from "@/components/ContactFormDialog";
import SvrafPocDialog from "@/components/SvrafPocDialog";
import EventFormDialog from "@/components/EventFormDialog";
import InlineNotesEditor from "@/components/InlineNotesEditor";
import EventsTab from "@/components/customer/EventsTab";
import TimelineTab from "@/components/customer/TimelineTab";
import ContactsTab from "@/components/customer/ContactsTab";
import OrdersTab from "@/components/customer/OrdersTab";
import AttachmentsTab from "@/components/customer/AttachmentsTab";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  PencilSimple,
  Trash,
  Plus,
  Buildings,
  Phone,
  EnvelopeSimple,
  MapPin,
  Star,
  CurrencyInr,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["Potential", "Engaged", "Negotiation", "Converted", "Lost"];

function formatINR(value) {
  const n = Number(value || 0);
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [activities, setActivities] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [pocOpen, setPocOpen] = useState(false);
  const [editingPoc, setEditingPoc] = useState(null);
  const [eventOpen, setEventOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, a, at, o, ev] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/activity`),
        api.get(`/customers/${id}/attachments`),
        api.get(`/orders`, { params: { customer_id: id } }),
        api.get(`/customers/${id}/events`),
      ]);
      setCustomer(c.data);
      setActivities(a.data);
      setAttachments(at.data);
      setOrders(o.data);
      setEvents(ev.data);
    } catch {
      toast.error("Failed to load customer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, [id]);

  const changeStatus = async (s) => {
    try {
      const { data } = await api.put(`/customers/${id}`, { status: s });
      setCustomer(data);
      load();
      toast.success(`Status set to ${s}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const saveNotes = async (notes) => {
    try {
      const { data } = await api.put(`/customers/${id}`, { notes });
      setCustomer(data);
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    }
  };

  // Attachments
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/customers/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [data, ...a]);
      toast.success("Uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      e.target.value = "";
    }
  };
  const deleteAttachment = async (aid) => {
    try {
      await api.delete(`/attachments/${aid}`);
      setAttachments((a) => a.filter((x) => x.id !== aid));
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };
  const downloadAttachment = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = att.file_name;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const deleteCustomer = async () => {
    if (!window.confirm("Delete this customer and all related orders/attachments?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      navigate("/sales/customers");
    } catch {
      toast.error("Delete failed");
    }
  };

  // Orders
  const updateOrderStatus = async (oid, s) => {
    try {
      await api.put(`/orders/${oid}`, { status: s });
      setOrders((arr) => arr.map((o) => (o.id === oid ? { ...o, status: s } : o)));
      toast.success("Order updated");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  // Contacts
  const submitContact = async (form) => {
    try {
      if (editingContact) {
        await api.put(`/customers/${id}/contacts/${editingContact.id}`, form);
        toast.success("Contact updated");
      } else {
        await api.post(`/customers/${id}/contacts`, form);
        toast.success("Contact added");
      }
      setContactOpen(false);
      setEditingContact(null);
      load();
    } catch {
      toast.error("Failed to save contact");
    }
  };
  const deleteContact = async (contactId) => {
    if (!window.confirm("Remove this contact?")) return;
    try {
      await api.delete(`/customers/${id}/contacts/${contactId}`);
      toast.success("Contact removed");
      load();
    } catch {
      toast.error("Failed");
    }
  };
  const setPrimaryContact = async (contactId) => {
    try {
      await api.put(`/customers/${id}/contacts/${contactId}`, { is_primary: true });
      toast.success("Primary contact updated");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  // SVRAF PoCs
  const submitPoc = async (form) => {
    try {
      if (editingPoc) {
        await api.put(`/customers/${id}/svraf-pocs/${editingPoc.id}`, form);
        toast.success("PoC updated");
      } else {
        await api.post(`/customers/${id}/svraf-pocs`, form);
        toast.success("PoC added");
      }
      setPocOpen(false);
      setEditingPoc(null);
      load();
    } catch {
      toast.error("Failed to save PoC");
    }
  };
  const deletePoc = async (pocId) => {
    if (!window.confirm("Remove this SVRAF PoC?")) return;
    try {
      await api.delete(`/customers/${id}/svraf-pocs/${pocId}`);
      toast.success("PoC removed");
      load();
    } catch {
      toast.error("Failed");
    }
  };
  const setPrimaryPoc = async (pocId) => {
    try {
      await api.put(`/customers/${id}/svraf-pocs/${pocId}`, { is_primary: true });
      toast.success("Primary PoC updated");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  // Events
  const submitEvent = async (form) => {
    try {
      if (editingEvent) {
        await api.put(`/events/${editingEvent.id}`, form);
        toast.success("Event updated");
      } else {
        await api.post(`/customers/${id}/events`, form);
        toast.success("Event logged");
      }
      setEventOpen(false);
      setEditingEvent(null);
      load();
    } catch {
      toast.error("Failed to save event");
    }
  };
  const deleteEvent = async (event) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/events/${event.id}`);
      setEvents((arr) => arr.filter((e) => e.id !== event.id));
      toast.success("Event deleted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  if (loading || !customer) {
    return (
      <div className="p-8 text-slate-500" data-testid="customer-detail-loading">
        Loading…
      </div>
    );
  }

  const pocs = customer.svraf_pocs || [];
  const sortedPocs = [...pocs].sort((a, b) =>
    a.is_primary === b.is_primary ? 0 : a.is_primary ? -1 : 1
  );

  return (
    <div data-testid="customer-detail-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate("/sales/customers")}
            className="p-2 rounded-md hover:bg-slate-100 text-slate-600"
            data-testid="back-to-customers"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              {customer.company || "Customer"}
            </div>
            <h1
              className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900"
              data-testid="customer-detail-name"
            >
              {customer.name}
            </h1>
          </div>
          {customer.opportunity_value > 0 && (
            <div
              className="hidden sm:flex items-center gap-2 px-3 h-10 rounded-md bg-emerald-50 border border-emerald-100 text-emerald-700"
              data-testid="opportunity-value-badge"
            >
              <CurrencyInr size={16} weight="bold" />
              <span className="text-sm font-semibold">
                {formatINR(customer.opportunity_value)}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-emerald-600">
                opportunity
              </span>
            </div>
          )}
          <Select value={customer.status} onValueChange={changeStatus}>
            <SelectTrigger
              className="w-44 bg-white border-slate-200 h-10"
              data-testid="customer-status-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="edit-customer-button"
          >
            <PencilSimple size={16} /> Edit
          </button>
          <button
            onClick={deleteCustomer}
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-red-100 bg-white text-red-600 text-sm font-medium hover:bg-red-50"
            data-testid="delete-customer-button"
          >
            <Trash size={16} />
          </button>
        </div>
      </div>

      <div className="p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-1 space-y-6">
            {/* Info card */}
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <StatusBadge status={customer.status} />
                <div className="text-xs text-slate-500">
                  Updated {new Date(customer.updated_at).toLocaleDateString()}
                </div>
              </div>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-start gap-3 text-slate-700">
                  <Buildings size={16} className="mt-0.5 text-slate-400" />
                  <span>{customer.company || "—"}</span>
                </div>
                <div className="flex items-start gap-3 text-slate-700">
                  <EnvelopeSimple size={16} className="mt-0.5 text-slate-400" />
                  <span>{customer.email || "—"}</span>
                </div>
                <div className="flex items-start gap-3 text-slate-700">
                  <Phone size={16} className="mt-0.5 text-slate-400" />
                  <span>{customer.phone || "—"}</span>
                </div>
                <div className="flex items-start gap-3 text-slate-700">
                  <MapPin size={16} className="mt-0.5 text-slate-400" />
                  <span>{customer.address || "—"}</span>
                </div>
                <div className="flex items-start gap-3 text-slate-700">
                  <CurrencyInr size={16} className="mt-0.5 text-slate-400" />
                  <span className="font-medium">
                    {customer.opportunity_value > 0
                      ? formatINR(customer.opportunity_value)
                      : "—"}
                    <span className="text-xs text-slate-500 font-normal ml-1">
                      opportunity value
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Notes card (inline editable) */}
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="notes-card">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Notes
                </div>
              </div>
              <InlineNotesEditor value={customer.notes} onSave={saveNotes} />
            </div>

            {/* SVRAF PoCs card */}
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="svraf-pocs-card">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  SVRAF Points of Contact
                </div>
                <button
                  onClick={() => {
                    setEditingPoc(null);
                    setPocOpen(true);
                  }}
                  className="inline-flex items-center gap-1 px-2 h-7 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
                  data-testid="add-svraf-poc-button"
                >
                  <Plus size={12} weight="bold" /> Add
                </button>
              </div>
              {sortedPocs.length === 0 ? (
                <div className="text-sm text-slate-400 italic py-3 text-center border border-dashed border-slate-200 rounded-md">
                  No PoCs assigned yet
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedPocs.map((p) => (
                    <div
                      key={p.id}
                      className={`rounded-md border p-3 ${
                        p.is_primary
                          ? "border-amber-200 bg-amber-50/50"
                          : "border-slate-200 bg-slate-50"
                      }`}
                      data-testid={`svraf-poc-${p.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-slate-900 text-sm">
                              {p.name}
                            </span>
                            {p.is_primary && (
                              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
                                <Star size={9} weight="fill" /> Primary
                              </span>
                            )}
                          </div>
                          {p.role && (
                            <div className="text-xs text-slate-500">{p.role}</div>
                          )}
                          {p.email && (
                            <a
                              href={`mailto:${p.email}`}
                              className="mt-1 inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 break-all"
                            >
                              <EnvelopeSimple size={12} /> {p.email}
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0">
                          {!p.is_primary && (
                            <button
                              onClick={() => setPrimaryPoc(p.id)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50"
                              title="Set as primary"
                              data-testid={`set-primary-poc-${p.id}`}
                            >
                              <Star size={13} />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setEditingPoc(p);
                              setPocOpen(true);
                            }}
                            className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-slate-100"
                            data-testid={`edit-svraf-poc-${p.id}`}
                          >
                            <PencilSimple size={13} />
                          </button>
                          <button
                            onClick={() => deletePoc(p.id)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-svraf-poc-${p.id}`}
                          >
                            <Trash size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Tabs */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="events" className="w-full">
              <TabsList
                className="h-auto bg-white border border-slate-200 rounded-md p-1 flex-wrap"
                data-testid="customer-tabs"
              >
                <TabsTrigger value="events" data-testid="tab-events" className="px-4 py-2">
                  Events ({events.length})
                </TabsTrigger>
                <TabsTrigger value="timeline" data-testid="tab-timeline" className="px-4 py-2">
                  Activity Timeline
                </TabsTrigger>
                <TabsTrigger value="contacts" data-testid="tab-contacts" className="px-4 py-2">
                  Contacts ({(customer.contacts || []).length})
                </TabsTrigger>
                <TabsTrigger value="orders" data-testid="tab-orders" className="px-4 py-2">
                  Orders ({orders.length})
                </TabsTrigger>
                <TabsTrigger
                  value="attachments"
                  data-testid="tab-attachments"
                  className="px-4 py-2"
                >
                  Files ({attachments.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="events" className="mt-5">
                <EventsTab
                  events={events}
                  onAdd={() => {
                    setEditingEvent(null);
                    setEventOpen(true);
                  }}
                  onEdit={(e) => {
                    setEditingEvent(e);
                    setEventOpen(true);
                  }}
                  onDelete={deleteEvent}
                />
              </TabsContent>

              <TabsContent value="timeline" className="mt-5">
                <TimelineTab activities={activities} />
              </TabsContent>

              <TabsContent value="contacts" className="mt-5">
                <ContactsTab
                  contacts={customer.contacts || []}
                  onAdd={() => {
                    setEditingContact(null);
                    setContactOpen(true);
                  }}
                  onEdit={(c) => {
                    setEditingContact(c);
                    setContactOpen(true);
                  }}
                  onDelete={deleteContact}
                  onSetPrimary={setPrimaryContact}
                />
              </TabsContent>

              <TabsContent value="orders" className="mt-5">
                <OrdersTab
                  orders={orders}
                  onAdd={() => setOrderOpen(true)}
                  onStatusChange={updateOrderStatus}
                />
              </TabsContent>

              <TabsContent value="attachments" className="mt-5">
                <AttachmentsTab
                  attachments={attachments}
                  onUpload={onUpload}
                  onDownload={downloadAttachment}
                  onDelete={deleteAttachment}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {editOpen && (
        <CustomerFormDialog
          open={editOpen}
          initial={customer}
          onClose={() => setEditOpen(false)}
          onSaved={(c) => {
            setEditOpen(false);
            setCustomer(c);
            load();
            toast.success("Customer updated");
          }}
        />
      )}
      {orderOpen && (
        <OrderFormDialog
          open={orderOpen}
          customerId={id}
          onClose={() => setOrderOpen(false)}
          onSaved={() => {
            setOrderOpen(false);
            load();
            toast.success("Order created");
          }}
        />
      )}
      {contactOpen && (
        <ContactFormDialog
          open={contactOpen}
          initial={editingContact}
          onClose={() => {
            setContactOpen(false);
            setEditingContact(null);
          }}
          onSubmit={submitContact}
        />
      )}
      {pocOpen && (
        <SvrafPocDialog
          open={pocOpen}
          initial={editingPoc}
          onClose={() => {
            setPocOpen(false);
            setEditingPoc(null);
          }}
          onSubmit={submitPoc}
        />
      )}
      {eventOpen && (
        <EventFormDialog
          open={eventOpen}
          initial={editingEvent}
          onClose={() => {
            setEventOpen(false);
            setEditingEvent(null);
          }}
          onSubmit={submitEvent}
        />
      )}
    </div>
  );
}
